import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import {
  ThunderboltOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  BarChartOutlined,
  DollarOutlined,
  GiftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';

const STATS = [
  { value: '500+', label: 'Wellness Centers' },
  { value: '50K+', label: 'Members Managed' },
  { value: '99.9%', label: 'Uptime' },
  { value: '< 1s', label: 'Biometric Match' },
];

const FEATURES = [
  {
    icon: <ThunderboltOutlined />,
    color: '#1890ff',
    bg: '#e6f4ff',
    title: 'Biometric Attendance',
    desc: 'Fingerprint-based automatic check-in in under a second. No cards, no PINs.',
  },
  {
    icon: <BarChartOutlined />,
    color: '#52c41a',
    bg: '#f6ffed',
    title: 'Real-time Dashboard',
    desc: 'Live stats on attendance, revenue, and member health — all in one view.',
  },
  {
    icon: <DollarOutlined />,
    color: '#fa8c16',
    bg: '#fff7e6',
    title: 'Payment Tracking',
    desc: 'Track deposits, pending fees, and generate payment reports effortlessly.',
  },
  {
    icon: <TeamOutlined />,
    color: '#722ed1',
    bg: '#f9f0ff',
    title: 'Coach Management',
    desc: 'Assign coaches to members, track performance, and reward top coaches.',
  },
  {
    icon: <SafetyCertificateOutlined />,
    color: '#eb2f96',
    bg: '#fff0f6',
    title: 'Health Measurements',
    desc: 'Log weight, BMI, body fat, blood pressure and track progress over time.',
  },
  {
    icon: <GiftOutlined />,
    color: '#13c2c2',
    bg: '#e6fffb',
    title: 'Smart Notifications',
    desc: 'Auto birthday wishes, absence alerts, and milestone push notifications.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Amin Patel',
    role: 'Owner, FitLife Center, Ujjain',
    text: 'Wellness transformed how we manage our 300+ members. The biometric check-in alone saved us 2 hours every day.',
    avatar: 'RS',
    color: '#1890ff',
  },
  {
    name: 'Vikas Saha',
    role: 'Manager, Zen Wellness, Ujjain',
    text: 'The payment tracking and coach management features are exactly what we needed. Setup was incredibly quick.',
    avatar: 'PM',
    color: '#52c41a',
  },
  {
    name: 'Rohit Solanki ',
    role: 'Director, HealthFirst, Ujjain',
    text: 'We manage 5 centers from one dashboard. The multi-center support with role-based access is a game changer.',
    avatar: 'AP',
    color: '#fa8c16',
  },
];

const CHECKLIST = [
  'No hardware lock-in — works with Mantra MFS500',
  'Multi-center management from one login',
  'Role-based access for admins and staff',
  'Mobile app for members with push notifications',
  'Automated email alerts for absences & birthdays',
  'Excel export for attendance & payment reports',
];

const Home = () => (
  <>
    <style>{`
      /* ── HERO ── */
      .hero {
        background: #001529;
        min-height: 92vh;
        display: flex; align-items: center; justify-content: center;
        padding: 80px 32px 60px;
        position: relative; overflow: hidden;
      }
      .hero-blob-1 {
        position: absolute; width: 500px; height: 500px; border-radius: 50%;
        background: radial-gradient(circle, rgba(24,144,255,0.18) 0%, transparent 70%);
        top: -120px; right: -100px; pointer-events: none;
      }
      .hero-blob-2 {
        position: absolute; width: 400px; height: 400px; border-radius: 50%;
        background: radial-gradient(circle, rgba(24,144,255,0.10) 0%, transparent 70%);
        bottom: -80px; left: -80px; pointer-events: none;
      }
      .hero-inner {
        max-width: 1200px; width: 100%; margin: 0 auto;
        display: grid; grid-template-columns: 1fr 1fr;
        gap: 64px; align-items: center; position: relative; z-index: 1;
      }
      .hero-badge {
        display: inline-flex; align-items: center; gap: 7px;
        background: rgba(24,144,255,0.15); border: 1px solid rgba(24,144,255,0.30);
        border-radius: 50px; padding: 6px 14px; margin-bottom: 24px;
        font-size: 12.5px; font-weight: 600; color: #1890ff; letter-spacing: 0.3px;
      }
      .hero-title {
        font-size: 52px; font-weight: 900; line-height: 1.08;
        color: #fff; letter-spacing: -1.5px; margin-bottom: 22px;
      }
      .hero-title span { color: #1890ff; }
      .hero-subtitle {
        font-size: 16px; line-height: 1.75; color: rgba(255,255,255,0.65);
        margin-bottom: 36px; max-width: 480px;
      }
      .hero-btns { display: flex; gap: 14px; flex-wrap: wrap; }
      .hero-btn-primary {
        height: 50px !important; padding: 0 28px !important;
        border-radius: 12px !important; font-size: 15px !important;
        font-weight: 700 !important;
        background: #1890ff !important; border-color: #1890ff !important;
        box-shadow: 0 6px 20px rgba(24,144,255,0.40) !important;
        transition: transform .15s !important;
      }
      .hero-btn-primary:hover { transform: translateY(-2px) !important; }
      .hero-btn-outline {
        height: 50px !important; padding: 0 28px !important;
        border-radius: 12px !important; font-size: 15px !important;
        font-weight: 600 !important; color: #fff !important;
        border: 1.5px solid rgba(255,255,255,0.28) !important;
        background: transparent !important;
        transition: border-color .15s, background .15s !important;
      }
      .hero-btn-outline:hover {
        border-color: #1890ff !important; background: rgba(24,144,255,0.08) !important;
      }
      /* hero card mockup */
      .hero-visual {
        display: flex; flex-direction: column; gap: 16px;
      }
      .hero-card {
        background: rgba(255,255,255,0.05);
        border: 1px solid rgba(255,255,255,0.10);
        border-radius: 16px; padding: 20px 24px;
        backdrop-filter: blur(8px);
      }
      .hero-card-title {
        font-size: 11px; text-transform: uppercase; letter-spacing: 1px;
        color: rgba(255,255,255,0.40); margin-bottom: 8px;
      }
      .hero-card-value {
        font-size: 28px; font-weight: 800; color: #fff; letter-spacing: -0.5px;
      }
      .hero-card-sub { font-size: 12px; color: rgba(255,255,255,0.45); margin-top: 2px; }
      .hero-card-row {
        display: flex; gap: 12px; margin-top: 6px;
      }
      .hero-pill {
        background: rgba(24,144,255,0.20); border-radius: 6px;
        padding: 4px 10px; font-size: 12px; color: #1890ff; font-weight: 600;
      }
      .hero-pill.green { background: rgba(82,196,26,0.20); color: #52c41a; }

      /* ── STATS ── */
      .stats-bar {
        background: #1890ff; padding: 28px 32px;
      }
      .stats-inner {
        max-width: 1200px; margin: 0 auto;
        display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;
        text-align: center;
      }
      .stat-value { font-size: 32px; font-weight: 900; color: #fff; letter-spacing: -1px; }
      .stat-label { font-size: 13px; color: rgba(255,255,255,0.80); margin-top: 2px; }

      /* ── SECTIONS ── */
      .pub-section { padding: 96px 32px; }
      .pub-section-alt { background: #f8fafc; }
      .pub-section-inner { max-width: 1200px; margin: 0 auto; }
      .section-eyebrow {
        font-size: 12px; font-weight: 700; letter-spacing: 1.5px;
        text-transform: uppercase; color: #1890ff; margin-bottom: 12px;
        display: block;
      }
      .section-title {
        font-size: 38px; font-weight: 800; color: #001529;
        letter-spacing: -0.8px; line-height: 1.18; margin-bottom: 16px;
      }
      .section-subtitle {
        font-size: 15.5px; color: #64748b; line-height: 1.7;
        max-width: 520px; margin-bottom: 56px;
      }

      /* ── FEATURES GRID ── */
      .features-grid {
        display: grid; grid-template-columns: repeat(3,1fr); gap: 24px;
      }
      .feature-card {
        background: #fff; border-radius: 16px; padding: 28px;
        border: 1px solid #e8edf2;
        transition: box-shadow .2s, transform .2s;
      }
      .feature-card:hover {
        box-shadow: 0 12px 32px rgba(0,21,41,0.10); transform: translateY(-4px);
      }
      .feature-icon-wrap {
        width: 48px; height: 48px; border-radius: 12px;
        display: flex; align-items: center; justify-content: center;
        font-size: 22px; margin-bottom: 16px;
      }
      .feature-card h3 {
        font-size: 16px; font-weight: 700; color: #1e293b; margin-bottom: 8px;
      }
      .feature-card p { font-size: 13.5px; color: #64748b; line-height: 1.65; margin: 0; }

      /* ── CHECKLIST ── */
      .checklist-wrap {
        display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
      }
      .checklist-item {
        display: flex; align-items: flex-start; gap: 10px;
        font-size: 14px; color: #334155; font-weight: 500;
      }

      /* ── TESTIMONIALS ── */
      .testi-grid {
        display: grid; grid-template-columns: repeat(3,1fr); gap: 24px;
      }
      .testi-card {
        background: #fff; border-radius: 16px; padding: 28px;
        border: 1px solid #e8edf2;
        display: flex; flex-direction: column; gap: 16px;
      }
      .testi-text {
        font-size: 14px; color: #475569; line-height: 1.72;
        font-style: italic; flex: 1;
      }
      .testi-author { display: flex; align-items: center; gap: 12px; }
      .testi-avatar {
        width: 42px; height: 42px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-size: 14px; font-weight: 700; color: #fff; flex-shrink: 0;
      }
      .testi-name { font-size: 14px; font-weight: 700; color: #1e293b; }
      .testi-role { font-size: 12px; color: #94a3b8; margin-top: 1px; }

      /* ── CTA BAND ── */
      .cta-band {
        background: #001529; padding: 80px 32px; text-align: center;
      }
      .cta-band h2 {
        font-size: 40px; font-weight: 900; color: #fff;
        letter-spacing: -1px; margin-bottom: 16px;
      }
      .cta-band p { font-size: 15px; color: rgba(255,255,255,0.60); margin-bottom: 36px; }

      /* ── RESPONSIVE ── */
      @media (max-width: 1024px) {
        .features-grid { grid-template-columns: repeat(2,1fr); }
        .testi-grid    { grid-template-columns: repeat(2,1fr); }
      }
      @media (max-width: 768px) {
        .hero-inner    { grid-template-columns: 1fr; gap: 40px; }
        .hero-visual   { display: none; }
        .hero-title    { font-size: 36px; }
        .stats-inner   { grid-template-columns: repeat(2,1fr); }
        .features-grid { grid-template-columns: 1fr; }
        .testi-grid    { grid-template-columns: 1fr; }
        .checklist-wrap{ grid-template-columns: 1fr; }
        .section-title { font-size: 28px; }
        .cta-band h2   { font-size: 28px; }
      }
    `}</style>

    {/* ═══════════ HERO ═══════════ */}
    <section className="hero">
      <div className="hero-blob-1" />
      <div className="hero-blob-2" />
      <div className="hero-inner">

        {/* Left copy */}
        <div>
          <div className="hero-badge">
            <ThunderboltOutlined /> Biometric-Powered Wellness Management
          </div>
          <h1 className="hero-title">
            Manage Your<br />
            Wellness Center<br />
            <span>Smarter.</span>
          </h1>
          <p className="hero-subtitle">
            Wellness is the all-in-one platform for fitness and wellness
            centers — with fingerprint attendance, health tracking, payment
            management, and smart notifications.
          </p>
          <div className="hero-btns">
            <Button type="primary" className="hero-btn-primary">
              <Link to="/contact" style={{ color: 'inherit', textDecoration: 'none' }}>
                Get Started Free <ArrowRightOutlined />
              </Link>
            </Button>
            <Button className="hero-btn-outline">
              <Link to="/features" style={{ color: 'inherit', textDecoration: 'none' }}>
                See Features
              </Link>
            </Button>
          </div>
        </div>

        {/* Right visual */}
        <div className="hero-visual">
          <div className="hero-card">
            <div className="hero-card-title">Today's Attendance</div>
            <div className="hero-card-value">247 / 312</div>
            <div className="hero-card-sub">Members checked in today</div>
            <div className="hero-card-row">
              <span className="hero-pill">🧬 Biometric: 231</span>
              <span className="hero-pill green">✅ 79% rate</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="hero-card">
              <div className="hero-card-title">Monthly Revenue</div>
              <div className="hero-card-value" style={{ fontSize: 22 }}>₹4.8L</div>
              <div className="hero-card-sub">↑ 12% vs last month</div>
            </div>
            <div className="hero-card">
              <div className="hero-card-title">Active Members</div>
              <div className="hero-card-value" style={{ fontSize: 22 }}>312</div>
              <div className="hero-card-sub">8 joined this week</div>
            </div>
          </div>
          <div className="hero-card">
            <div className="hero-card-title">Last Biometric Scan</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: 'rgba(24,144,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18
              }}>👤</div>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Rahul Sharma</div>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
                  ✅ Checked in · just now
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ═══════════ STATS ═══════════ */}
    <section className="stats-bar">
      <div className="stats-inner">
        {STATS.map((s, i) => (
          <div key={i}>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>
    </section>

    {/* ═══════════ FEATURES ═══════════ */}
    <section className="pub-section pub-section-alt">
      <div className="pub-section-inner">
        <span className="section-eyebrow">What We Offer</span>
        <h2 className="section-title">Everything your center needs</h2>
        <p className="section-subtitle">
          From biometric check-ins to payment reports — Wellness handles the
          operations so your team can focus on members.
        </p>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon-wrap" style={{ background: f.bg, color: f.color }}>
                {f.icon}
              </div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ═══════════ WHY US ═══════════ */}
    <section className="pub-section">
      <div className="pub-section-inner" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
        <div>
          <span className="section-eyebrow">Why Wellness</span>
          <h2 className="section-title">Built for Indian wellness centers</h2>
          <p className="section-subtitle" style={{ marginBottom: 32 }}>
            We understand the unique needs of gyms, yoga studios, and wellness
            centers across India — and we built every feature with that in mind.
          </p>
          <div className="checklist-wrap">
            {CHECKLIST.map((item, i) => (
              <div key={i} className="checklist-item">
                <CheckCircleFilled style={{ color: '#1890ff', fontSize: 16, marginTop: 2, flexShrink: 0 }} />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 36 }}>
            <Button type="primary" size="large" style={{
              borderRadius: 10, background: '#001529', borderColor: '#001529',
              fontWeight: 700, height: 48, padding: '0 28px'
            }}>
              <Link to="/features" style={{ color: 'inherit', textDecoration: 'none' }}>
                Explore All Features <ArrowRightOutlined />
              </Link>
            </Button>
          </div>
        </div>
        <div style={{
          background: '#f8fafc', borderRadius: 20, padding: 36,
          border: '1px solid #e2e8f0',
        }}>
          {[
            { label: 'Attendance Rate', value: 79, color: '#1890ff' },
            { label: 'Payment Collection', value: 92, color: '#52c41a' },
            { label: 'Member Retention', value: 86, color: '#fa8c16' },
          ].map((item, i) => (
            <div key={i} style={{ marginBottom: i < 2 ? 28 : 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>{item.label}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: item.color }}>{item.value}%</span>
              </div>
              <div style={{ background: '#e2e8f0', borderRadius: 99, height: 8 }}>
                <div style={{
                  width: `${item.value}%`, height: '100%',
                  background: item.color, borderRadius: 99,
                  transition: 'width 1s ease',
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ═══════════ TESTIMONIALS ═══════════ */}
    <section className="pub-section pub-section-alt">
      <div className="pub-section-inner">
        <span className="section-eyebrow">Testimonials</span>
        <h2 className="section-title">Trusted by wellness professionals</h2>
        <p className="section-subtitle">
          Hear from center owners and managers who transformed their operations
          with Wellness.
        </p>
        <div className="testi-grid">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="testi-card">
              <p className="testi-text">"{t.text}"</p>
              <div className="testi-author">
                <div className="testi-avatar" style={{ background: t.color }}>{t.avatar}</div>
                <div>
                  <div className="testi-name">{t.name}</div>
                  <div className="testi-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ═══════════ CTA BAND ═══════════ */}
    <section className="cta-band">
      <h2>Ready to modernize your wellness center?</h2>
      <p>Join hundreds of centers already using Wellness to automate operations.</p>
      <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button type="primary" size="large" style={{
          borderRadius: 12, background: '#1890ff', borderColor: '#1890ff',
          fontWeight: 700, height: 52, padding: '0 32px', fontSize: 15,
          boxShadow: '0 6px 20px rgba(24,144,255,0.40)'
        }}>
          <Link to="/contact" style={{ color: 'inherit', textDecoration: 'none' }}>
            Get Started Today <ArrowRightOutlined />
          </Link>
        </Button>
        <Button size="large" style={{
          borderRadius: 12, background: 'transparent',
          border: '1.5px solid rgba(255,255,255,0.25)', color: '#fff',
          fontWeight: 600, height: 52, padding: '0 32px', fontSize: 15,
        }}>
          <Link to="/about" style={{ color: 'inherit', textDecoration: 'none' }}>
            Learn About Us
          </Link>
        </Button>
      </div>
    </section>
  </>
);

export default Home;
